import React, { useEffect, useMemo, useState } from 'react';
import { BarChart2Icon, PieChartIcon, DownloadIcon, FilterIcon, UsersIcon, CheckCircleIcon, XCircleIcon, HelpCircleIcon, CalendarIcon, BriefcaseIcon, ChevronDownIcon, ChevronRightIcon, UserIcon } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { intentionsService } from '../../services/intentionsService';
import { intentionPeriodsService } from '../../services/intentionPeriodsService';
import { staffService, StaffMember } from '../../services/staffService';
import { TeachingIntention } from '../../types/intentions';
import { exportIntentions } from '../../utils/exportIntentions';
import IntentionsSubmissionsTable from './components/IntentionsSubmissionsTable';
import { useToast } from '../../contexts/ToastContext';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';
import { TEACHING_ROLES, getStaffCode } from '../../utils/intentionStats';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
// Categorical chart palette drawn from the design tokens (src/styles/tokens.css).
// Recharts needs literal colour values, so these mirror the token hexes rather
// than Tailwind classes. Order matters for the Return Intentions pie, whose
// slices are Returning / Not Returning / Uncertain / No Response — mapped here to
// ok (green) / danger (red) / warn (amber) / ink (grey) so status reads correctly.
const COLORS = [
  '#2E8B5E', // ok-solid    — returning
  '#B3261E', // danger-solid — not returning
  '#C99213', // warn-solid  — uncertain
  '#7A8493', // ink-400     — no response
  '#2E6BAC', // navy-500
  '#1F5AA0', // info-solid
  '#00234B', // navy-900
  '#D4AF37', // gold-400
];
// TEACHING_ROLES and getStaffCode are imported from utils/intentionStats so this
// report and the /intentions, Review and dashboard stats share one definition.
const TeachingIntentionsReports: React.FC = () => {
  const {
    addToast
  } = useToast();
  const { learningAreaNames } = useLearningAreas();
  const [intentions, setIntentions] = useState<TeachingIntention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Resolved once from the active collection period (see the effect below) so the
  // report opens on a year that actually has intentions, never a hardcoded future
  // year with no rows. `null` = not yet resolved; the first fetch waits for it.
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [exportLoading, setExportLoading] = useState<'csv' | 'excel' | 'pdf' | null>(null);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  // Active staff roster (used by the "Not Submitted" tab to find non-responders).
  const [activeStaff, setActiveStaff] = useState<StaffMember[]>([]);
  // Which staff are *expected* to submit: teaching roles only (default) or every active staff member.
  const [staffGroup, setStaffGroup] = useState<'teaching' | 'all'>('teaching');
  // Resolve which year to open on BEFORE the first fetch. The active collection
  // period is the correct default (that's the year the tool is gathering now);
  // fall back to the most recent period, then to next calendar year. This avoids
  // opening on an empty future year when the data lives in the current period.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let year = new Date().getFullYear() + 1; // last-resort fallback
      try {
        const open = await intentionPeriodsService.getCurrentOpenPeriod();
        if (open?.academicYear) {
          year = open.academicYear;
        } else {
          const periods = await intentionPeriodsService.getAllPeriods();
          const years = periods.map(p => p.academicYear).filter(Boolean);
          if (years.length > 0) year = Math.max(...years);
        }
      } catch (err) {
        console.error('Could not resolve default intentions year; using fallback:', err);
      }
      if (!cancelled) setSelectedYear(year);
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // Fetch intentions once the year is resolved, and again whenever it changes.
  useEffect(() => {
    if (selectedYear == null) return;
    fetchIntentions();
  }, [selectedYear]);
  // Load the active-staff roster once on mount (independent of the selected year).
  useEffect(() => {
    staffService
      .getAllStaff()
      .then(all => setActiveStaff(all.filter(s => s.status === 'active')))
      .catch(err => console.error('Error loading staff roster for Not Submitted report:', err));
  }, []);
  // Fetch intentions from the service
  const fetchIntentions = async () => {
    if (selectedYear == null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await intentionsService.getIntentionsByYear(selectedYear);
      setIntentions(data);
    } catch (err) {
      console.error('Error fetching intentions:', err);
      setError('Failed to load teaching intentions. Please try again.');
      // Fall back to empty array
      setIntentions([]);
    } finally {
      setLoading(false);
    }
  };
  // Filter intentions by department if one is selected
  const filteredIntentions = useMemo(() => {
    if (selectedDepartment === 'all') {
      return intentions;
    }
    return intentions.filter(intention => intention.department === selectedDepartment);
  }, [intentions, selectedDepartment]);
  // Get unique departments and years from intentions
  const departments = useMemo(() => {
    const depts = [...new Set(intentions.map(i => i.department))].sort();
    return ['all', ...depts];
  }, [intentions]);

  // Get available years dynamically
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = new Set<number>();
    
    // Add current year and next few years
    for (let i = -1; i <= 3; i++) {
      years.add(currentYear + i);
    }
    
    // Add any years found in existing data
    intentions.forEach(intention => {
      if (intention.year) {
        years.add(intention.year);
      }
    });
    // Always include the resolved/selected year so the dropdown never renders a
    // value that isn't one of its options.
    if (selectedYear) years.add(selectedYear);

    return Array.from(years).sort((a, b) => b - a); // Sort descending
  }, [intentions, selectedYear]);
  // Calculate overview statistics
  const overviewStats = useMemo(() => {
    const totalStaff = filteredIntentions.length;
    const returning = filteredIntentions.filter(i => i.intentionToReturn === 'yes').length;
    const notReturning = filteredIntentions.filter(i => i.intentionToReturn === 'no').length;
    const uncertain = filteredIntentions.filter(i => i.intentionToReturn === 'uncertain').length;
    const noResponse = totalStaff - returning - notReturning - uncertain;
    const totalCurrentFTE = filteredIntentions.reduce((sum, i) => sum + i.currentFTE, 0);
    const returningStaffFTE = filteredIntentions.filter(i => i.intentionToReturn === 'yes').reduce((sum, i) => sum + i.currentFTE, 0);
    const totalTargetFTE = filteredIntentions.filter(i => i.intentionToReturn === 'yes').reduce((sum, i) => sum + i.targetFTE, 0);
    const fteDifference = totalTargetFTE - returningStaffFTE; // More accurate: compare returning staff's current vs target
    const leavingStaffFTE = filteredIntentions.filter(i => i.intentionToReturn === 'no').reduce((sum, i) => sum + i.currentFTE, 0);
    const uncertainStaffFTE = filteredIntentions.filter(i => i.intentionToReturn === 'uncertain').reduce((sum, i) => sum + i.currentFTE, 0);
    
    return {
      totalStaff,
      returning,
      notReturning,
      uncertain,
      noResponse,
      totalCurrentFTE: totalCurrentFTE.toFixed(1),
      returningStaffFTE: returningStaffFTE.toFixed(1),
      totalTargetFTE: totalTargetFTE.toFixed(1),
      fteDifference: fteDifference.toFixed(1),
      leavingStaffFTE: leavingStaffFTE.toFixed(1),
      uncertainStaffFTE: uncertainStaffFTE.toFixed(1),
      returningPercentage: totalStaff > 0 ? Math.round(returning / totalStaff * 100) : 0,
      retentionRisk: uncertain + noResponse // Staff at risk of leaving
    };
  }, [filteredIntentions]);
  // Prepare data for return intentions pie chart
  const returnIntentionsData = useMemo(() => [{
    name: 'Returning',
    value: overviewStats.returning
  }, {
    name: 'Not Returning',
    value: overviewStats.notReturning
  }, {
    name: 'Uncertain',
    value: overviewStats.uncertain
  }, {
    name: 'No Response',
    value: overviewStats.noResponse
  }].filter(item => item.value > 0), [overviewStats]);
  // Prepare data for department breakdown
  const departmentData = useMemo(() => {
    const deptCounts: Record<string, {
      total: number;
      returning: number;
      notReturning: number;
      uncertain: number;
    }> = {};
    intentions.forEach(intention => {
      if (!deptCounts[intention.department]) {
        deptCounts[intention.department] = {
          total: 0,
          returning: 0,
          notReturning: 0,
          uncertain: 0
        };
      }
      deptCounts[intention.department].total++;
      if (intention.intentionToReturn === 'yes') {
        deptCounts[intention.department].returning++;
      } else if (intention.intentionToReturn === 'no') {
        deptCounts[intention.department].notReturning++;
      } else if (intention.intentionToReturn === 'uncertain') {
        deptCounts[intention.department].uncertain++;
      }
    });
    return Object.entries(deptCounts).map(([dept, counts]) => ({
      department: dept,
      ...counts,
      returningPercentage: counts.total > 0 ? Math.round(counts.returning / counts.total * 100) : 0
    })).sort((a, b) => b.total - a.total);
  }, [intentions]);
  // Prepare data for department bar chart
  const departmentBarData = useMemo(() => {
    return departmentData.slice(0, 8).map(dept => ({
      name: dept.department,
      value: dept.total
    }));
  }, [departmentData]);
  // Prepare data for employment type breakdown
  const employmentTypeData = useMemo(() => {
    const employmentCounts: Record<string, number> = {
      'full-time': 0,
      'part-time': 0,
      casual: 0,
      'fixed-term': 0,
      ongoing: 0
    };
    filteredIntentions.forEach(intention => {
      if (employmentCounts[intention.currentEmploymentType] !== undefined) {
        employmentCounts[intention.currentEmploymentType]++;
      }
    });
    return Object.entries(employmentCounts).filter(([_, count]) => count > 0).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' '),
      value: count
    }));
  }, [filteredIntentions]);
  // ============================================
  // "Not Submitted" report
  // ============================================
  const notSubmittedReport = useMemo(() => {
    // A 'draft' row means started-but-not-submitted; any other status means a
    // submission exists for the year.
    const submittedCodes = new Set(
      intentions
        .filter(i => i.status !== 'draft')
        .map(i => (i.staffId || '').toString().trim().toUpperCase())
    );
    const draftCodes = new Set(
      intentions
        .filter(i => i.status === 'draft')
        .map(i => (i.staffId || '').toString().trim().toUpperCase())
    );
    // Who is expected to submit?
    const expected = activeStaff.filter(s =>
      staffGroup === 'all' ? true : TEACHING_ROLES.includes(s.role || 'teacher')
    );
    // Anyone whose code isn't in the submitted set hasn't submitted yet.
    const notSubmitted = expected
      .filter(s => !submittedCodes.has(getStaffCode(s)))
      .map(s => ({
        id: s.id,
        name: s.name,
        department: s.department || 'Unassigned',
        role: s.role || 'teacher',
        email: s.email || '',
        // Distinguish "started a draft" from "never engaged".
        state: draftCodes.has(getStaffCode(s)) ? ('draft' as const) : ('none' as const)
      }))
      // Alphabetical by name across the whole cohort.
      .sort((a, b) => a.name.localeCompare(b.name));
    const expectedCount = expected.length;
    const notSubmittedCount = notSubmitted.length;
    const submittedCount = expectedCount - notSubmittedCount;
    return {
      list: notSubmitted,
      expectedCount,
      notSubmittedCount,
      submittedCount,
      draftCount: notSubmitted.filter(s => s.state === 'draft').length,
      submissionRate: expectedCount > 0 ? Math.round((submittedCount / expectedCount) * 100) : 0
    };
  }, [intentions, activeStaff, staffGroup]);
  // Export the "Not Submitted" list as a CSV for follow-up.
  const handleExportNotSubmitted = () => {
    try {
      const escape = (v: string) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
      const header = ['Name', 'Department', 'Role', 'Email', 'Status'];
      const rows = notSubmittedReport.list.map(s => [
        s.name,
        s.department,
        s.role,
        s.email,
        s.state === 'draft' ? 'Draft started (not submitted)' : 'Not started'
      ]);
      const csv = [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `teaching-intentions-not-submitted-${selectedYear}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      addToast(`Exported ${notSubmittedReport.notSubmittedCount} staff who haven't submitted`, 'success');
    } catch (error) {
      console.error('Export failed:', error);
      addToast('Export failed. Please try again.', 'error');
    }
  };
  // Toggle department expansion
  const toggleDepartmentExpansion = (department: string) => {
    const newExpanded = new Set(expandedDepartments);
    if (newExpanded.has(department)) {
      newExpanded.delete(department);
    } else {
      newExpanded.add(department);
    }
    setExpandedDepartments(newExpanded);
  };

  // Handle export data
  const handleExportData = async (format: 'csv' | 'excel' | 'pdf') => {
    try {
      setExportLoading(format);
      await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for UX
      exportIntentions(filteredIntentions, format);
      addToast(`Successfully exported ${filteredIntentions.length} teaching intentions as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Export failed:', error);
      addToast('Export failed. Please try again.', 'error');
    } finally {
      setExportLoading(null);
    }
  };
  // Render loading state
  if (loading) {
    return <div>
        <PageHeader title="Teaching Intentions Reports" description="Who intends to return next year, at what FTE, and who still needs to submit." />
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <Card>
            <div className="p-12 flex justify-center">
              <Spinner size="lg" label="Loading teaching intentions data…" />
            </div>
          </Card>
        </div>
      </div>;
  }
  // Render error state
  if (error) {
    return <div>
        <PageHeader title="Teaching Intentions Reports" description="Who intends to return next year, at what FTE, and who still needs to submit." />
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <Card>
            <div className="p-6 text-center">
              <div className="text-danger-fg mx-auto">
                <XCircleIcon className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="mt-2 text-card-title text-ink-900">
                Error Loading Data
              </h3>
              <p className="mt-1 text-ink-500">{error}</p>
              <div className="mt-6">
                <Button variant="primary" onClick={fetchIntentions}>
                  Try Again
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>;
  }
  return <div>
      <PageHeader title="Teaching Intentions Reports" description="Analysis and reports of teaching intentions data" actions={<div className="flex space-x-2">
            {/* Export Dropdown */}
            <div className="relative inline-block text-left">
              <select 
                className="inline-flex justify-center w-full rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-body-sm font-medium text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                onChange={(e) => {
                  if (e.target.value) {
                    handleExportData(e.target.value as 'csv' | 'excel' | 'pdf');
                    e.target.value = ''; // Reset selection
                  }
                }}
                disabled={exportLoading !== null}
                value=""
              >
                <option value="" disabled>
                  {exportLoading ? `Exporting ${exportLoading.toUpperCase()}...` : 'Export Data'}
                </option>
                <option value="csv">Export as CSV</option>
                <option value="excel">Export as Excel</option>
                <option value="pdf">Export as PDF</option>
              </select>
            </div>
          </div>} />
      <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <Card className="mb-6">
          <div className="p-4 flex flex-wrap items-center gap-4">
            <div>
              <label htmlFor="year-select" className="block text-body-sm font-medium text-ink-700 mb-1">
                Academic Year
              </label>
              <select id="year-select" value={selectedYear ?? ''} onChange={e => setSelectedYear(Number(e.target.value))} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md">
                {availableYears.map(year => <option key={year} value={year}>
                    {year}
                  </option>)}
              </select>
            </div>
            <div>
              <label htmlFor="department-select" className="block text-body-sm font-medium text-ink-700 mb-1">
                Learning Area
              </label>
              <select id="department-select" value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md">
                <option value="all">All Learning Areas</option>
                {learningAreaNames.map(name => <option key={name} value={name}>
                    {name}
                  </option>)}
              </select>
            </div>
            <div className="ml-auto">
              <Button variant="primary" onClick={fetchIntentions}>
                Update Report
              </Button>
            </div>
          </div>
        </Card>
        {/* No intentions for this year — honest empty state that keeps the year
            selector above reachable so the user can switch to a year with data. */}
        {intentions.length === 0 ? (
          <Card>
            <div className="p-12 text-center">
              <CalendarIcon className="h-12 w-12 text-ink-300 mx-auto" />
              <h3 className="mt-3 text-card-title text-ink-900">
                No teaching intentions for {selectedYear}
              </h3>
              <p className="mt-1 text-body-sm text-ink-500 max-w-md mx-auto">
                Nobody has submitted a teaching intention for this academic year yet.
                Choose a different year from the selector above to see submitted data.
              </p>
            </div>
          </Card>
        ) : (
        <>
        {/* Report Tabs */}
        <div className="mb-6 border-b border-ink-200">
          <nav className="-mb-px flex space-x-8">
            <button onClick={() => setActiveTab('overview')} className={`${activeTab === 'overview' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Overview
            </button>
            <button onClick={() => setActiveTab('departments')} className={`${activeTab === 'departments' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Departments
            </button>
            <button onClick={() => setActiveTab('notSubmitted')} className={`${activeTab === 'notSubmitted' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Not Submitted
              {notSubmittedReport.notSubmittedCount > 0 && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-meta font-semibold bg-danger-bg text-danger-fg font-mono tabular-nums">
                  {notSubmittedReport.notSubmittedCount}
                </span>}
            </button>
            <button onClick={() => setActiveTab('staffing')} className={`${activeTab === 'staffing' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Staffing
            </button>
            <button onClick={() => setActiveTab('preferences')} className={`${activeTab === 'preferences' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Preferences
            </button>
            <button onClick={() => setActiveTab('insights')} className={`${activeTab === 'insights' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Insights
            </button>
            <button onClick={() => setActiveTab('submissions')} className={`${activeTab === 'submissions' ? 'border-navy-500 text-navy-600' : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-body-sm`}>
              Submissions
            </button>
          </nav>
        </div>
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-navy-500 rounded-md p-3">
                      <UsersIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          Intentions on file
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {overviewStats.totalStaff}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-ok-solid rounded-md p-3">
                      <CheckCircleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          Returning Staff
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {overviewStats.returning}
                          </div>
                          <div className="ml-2 flex items-baseline text-body-sm font-semibold text-ok-fg">
                            <span className="font-mono tabular-nums">{overviewStats.returningPercentage}%</span>
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-danger-solid rounded-md p-3">
                      <XCircleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          Not Returning
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {overviewStats.notReturning}
                          </div>
                          <div className="ml-2 flex items-baseline text-body-sm font-semibold text-danger-fg">
                            <span className="font-mono tabular-nums">
                              {overviewStats.totalStaff > 0 ? Math.round(overviewStats.notReturning / overviewStats.totalStaff * 100) : 0}
                              %
                            </span>
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-warn-solid rounded-md p-3">
                      <HelpCircleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          Uncertain
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {overviewStats.uncertain}
                          </div>
                          <div className="ml-2 flex items-baseline text-body-sm font-semibold text-warn-fg">
                            <span className="font-mono tabular-nums">
                              {overviewStats.totalStaff > 0 ? Math.round(overviewStats.uncertain / overviewStats.totalStaff * 100) : 0}
                              %
                            </span>
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Additional Insights */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-navy-500 rounded-md p-3">
                      <HelpCircleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          Completion Rate
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {notSubmittedReport.submissionRate}%
                          </div>
                          <div className="ml-2 text-body-sm text-ink-500">
                            {notSubmittedReport.submittedCount} of {notSubmittedReport.expectedCount} submitted
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
              
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-warn-solid rounded-md p-3">
                      <BriefcaseIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          At-Risk FTE
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {(Number(overviewStats.leavingStaffFTE) + Number(overviewStats.uncertainStaffFTE)).toFixed(1)}
                          </div>
                          <div className="ml-2 text-body-sm text-ink-500">
                            leaving/uncertain
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
              
              <Card>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-info-bg0 rounded-md p-3">
                      <CheckCircleIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-body-sm font-medium text-ink-500 truncate">
                          Retention Rate
                        </dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                            {overviewStats.totalStaff > 0 ? Math.round((overviewStats.returning / overviewStats.totalStaff) * 100) : 0}%
                          </div>
                          <div className="ml-2 text-body-sm text-ink-500">
                            confirmed returning
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* FTE Summary */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  FTE Summary for {selectedYear}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-navy-500 rounded-md p-3">
                          <BriefcaseIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-body-sm font-medium text-ink-500 truncate">
                              Current FTE
                            </dt>
                            <dd className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                              {overviewStats.totalCurrentFTE}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-ok-solid rounded-md p-3">
                          <BriefcaseIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-body-sm font-medium text-ink-500 truncate">
                              Projected FTE (Returning)
                            </dt>
                            <dd className="text-2xl font-semibold text-ink-900 font-mono tabular-nums">
                              {overviewStats.totalTargetFTE}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 ${Number(overviewStats.fteDifference) >= 0 ? 'bg-ok-solid' : 'bg-danger-solid'} rounded-md p-3`}>
                          <BriefcaseIcon className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-body-sm font-medium text-ink-500 truncate">
                              FTE Change
                            </dt>
                            <dd className={`text-2xl font-semibold font-mono tabular-nums ${Number(overviewStats.fteDifference) >= 0 ? 'text-ok-fg' : 'text-danger-fg'}`}>
                              {Number(overviewStats.fteDifference) >= 0 ? '+' : ''}
                              {overviewStats.fteDifference}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            {/* Return Intentions Chart */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                    Return Intentions
                  </h3>
                  <div className="py-4 h-64">
                    {returnIntentionsData.length > 0 ? <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={returnIntentionsData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" nameKey="name" label={({
                      name,
                      percent
                    }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                            {returnIntentionsData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer> : <div className="flex items-center justify-center h-full">
                        <p className="text-ink-500">No data available</p>
                      </div>}
                  </div>
                </div>
              </Card>
              <Card>
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                    Employment Types
                  </h3>
                  <div className="py-4 h-64">
                    {employmentTypeData.length > 0 ? <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={employmentTypeData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" nameKey="name" label={({
                      name,
                      percent
                    }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                            {employmentTypeData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer> : <div className="flex items-center justify-center h-full">
                        <p className="text-ink-500">No data available</p>
                      </div>}
                  </div>
                </div>
              </Card>
            </div>
          </div>}
        {/* Not Submitted Tab */}
        {activeTab === 'notSubmitted' && <div className="space-y-6">
            {/* Summary + controls */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="text-card-title leading-6 text-ink-900">
                      Staff who haven't submitted — {selectedYear}
                    </h3>
                    <p className="mt-1 text-body-sm text-ink-500">
                      {notSubmittedReport.submittedCount} of {notSubmittedReport.expectedCount}{' '}
                      {staffGroup === 'teaching' ? 'expected teaching staff' : 'active staff'} have submitted
                      ({notSubmittedReport.submissionRate}%).{' '}
                      <span className="font-medium text-ink-700">
                        {notSubmittedReport.notSubmittedCount} still outstanding
                      </span>
                      {notSubmittedReport.draftCount > 0 && <> ({notSubmittedReport.draftCount} have a saved draft).</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Staff group toggle */}
                    <div>
                      <label htmlFor="staff-group-select" className="sr-only">Staff group</label>
                      <select id="staff-group-select" value={staffGroup} onChange={e => setStaffGroup(e.target.value as 'teaching' | 'all')} className="block w-full pl-3 pr-10 py-2 text-body-sm border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 rounded-md">
                        <option value="teaching">Teaching roles only</option>
                        <option value="all">All active staff</option>
                      </select>
                    </div>
                    <Button variant="secondary" onClick={handleExportNotSubmitted} disabled={notSubmittedReport.notSubmittedCount === 0}>
                      <DownloadIcon className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
                <div className="mt-4 bg-navy-50 border-l-4 border-navy-400 p-4">
                  <p className="text-body-sm text-navy-700">
                    "Not submitted" means there is no submitted teaching intention for {selectedYear}.
                    A staff member with a saved but un-submitted draft is shown as <span className="font-medium">Draft started</span>.
                    {staffGroup === 'teaching'
                      ? ' Non-teaching roles (admin, principal) are excluded — switch to "All active staff" to include everyone.'
                      : ' Showing every active staff member, including non-teaching roles.'}
                  </p>
                </div>
              </div>
            </Card>
            {/* Not-submitted list — alphabetical by name */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                {activeStaff.length === 0 ? <div className="text-center py-8 text-ink-500">
                    Loading staff roster…
                  </div> : notSubmittedReport.notSubmittedCount === 0 ? <div className="text-center py-8">
                    <CheckCircleIcon className="h-12 w-12 text-ok-fg mx-auto" />
                    <p className="mt-2 text-body-sm font-medium text-ink-900">Everyone has submitted</p>
                    <p className="text-body-sm text-ink-500">
                      All {notSubmittedReport.expectedCount}{' '}
                      {staffGroup === 'teaching' ? 'expected teaching staff' : 'active staff'} have a submitted intention for {selectedYear}.
                    </p>
                  </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {notSubmittedReport.list.map(s => <div key={s.id} className={`p-3 rounded-md border ${s.state === 'draft' ? 'bg-warn-bg border-warn-border' : 'bg-ink-50 border-ink-200'}`}>
                        <div className="flex items-center space-x-2">
                          <UserIcon className={`h-4 w-4 ${s.state === 'draft' ? 'text-warn-solid' : 'text-ink-400'}`} />
                          <span className="text-body-sm font-medium text-ink-900">{s.name}</span>
                        </div>
                        <div className="mt-1 text-meta text-ink-500 truncate">
                          {displayLearningArea(s.department)}{s.email ? ` · ${s.email}` : ''}
                        </div>
                        <div className="mt-1">
                          <span className={`px-2 py-0.5 inline-flex text-meta leading-5 font-semibold rounded-full ${s.state === 'draft' ? 'bg-warn-bg text-warn-fg' : 'bg-ink-200 text-ink-700'}`}>
                            {s.state === 'draft' ? 'Draft started' : 'Not started'}
                          </span>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </Card>
          </div>}
        {/* Departments Tab */}
        {activeTab === 'departments' && <div className="space-y-6">
            {/* Department Distribution Chart */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Learning Area Distribution
                </h3>
                <div className="h-80">
                  {departmentBarData.length > 0 ? <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={departmentBarData} margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="value" name="Staff Count" fill="#2E6BAC" />
                      </BarChart>
                    </ResponsiveContainer> : <div className="flex items-center justify-center h-full">
                      <p className="text-ink-500">No data available</p>
                    </div>}
                </div>
              </div>
            </Card>
            {/* Department Breakdown Table */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Learning Area Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Total Staff
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Returning
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Not Returning
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Uncertain
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Return Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {departmentData.map((dept, index) => <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm font-medium text-ink-900">
                            {displayLearningArea(dept.department)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.returning}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.notReturning}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                            {dept.uncertain}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                            <div className="flex items-center">
                              <span className="mr-2 font-mono tabular-nums">
                                {dept.returningPercentage}%
                              </span>
                              <div className="w-24 bg-ink-200 rounded-full h-2.5">
                                <div className={`h-2.5 rounded-full ${dept.returningPercentage >= 75 ? 'bg-ok-solid' : dept.returningPercentage >= 50 ? 'bg-warn-solid' : 'bg-danger-solid'}`} style={{
                            width: `${dept.returningPercentage}%`
                          }}></div>
                              </div>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </div>}
        {/* Staffing Tab */}
        {activeTab === 'staffing' && <div className="space-y-6">
            {/* Staffing Requirements */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Staffing Requirements for {selectedYear}
                </h3>
                <div className="bg-warn-bg border-l-4 border-warn-border p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <HelpCircleIcon className="h-5 w-5 text-warn-solid" />
                    </div>
                    <div className="ml-3">
                      <p className="text-body-sm text-warn-fg">
                        This section shows projected staffing needs based on current intentions data. 
                        Click on department names to see individual staff changes.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Current FTE
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Projected FTE
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Difference
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {departmentData.map((dept, index) => {
                        // Calculate FTE for this department
                        const deptIntentions = intentions.filter(i => i.department === dept.department);
                        const currentFTE = deptIntentions.reduce((sum, i) => sum + i.currentFTE, 0);
                        const projectedFTE = deptIntentions.filter(i => i.intentionToReturn === 'yes').reduce((sum, i) => sum + i.targetFTE, 0);
                        const difference = projectedFTE - currentFTE;
                        const isExpanded = expandedDepartments.has(dept.department);
                        
                        // Categorize staff by their changes
                        const returningStaff = deptIntentions.filter(i => i.intentionToReturn === 'yes');
                        const leavingStaff = deptIntentions.filter(i => i.intentionToReturn === 'no');
                        const uncertainStaff = deptIntentions.filter(i => i.intentionToReturn === 'uncertain');
                        const noResponseStaff = deptIntentions.filter(i => !i.intentionToReturn);
                        const staffWithFTEChanges = returningStaff.filter(i => Math.abs(i.targetFTE - i.currentFTE) > 0.05);
                        
                        return (
                          <React.Fragment key={index}>
                            {/* Department Summary Row */}
                            <tr className={`${index > 0 ? 'border-t border-ink-200' : ''} hover:bg-ink-50`}>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm font-medium text-ink-900">
                                <button
                                  onClick={() => toggleDepartmentExpansion(dept.department)}
                                  className="flex items-center space-x-2 text-left hover:text-navy-600"
                                >
                                  {isExpanded ? (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronRightIcon className="h-4 w-4" />
                                  )}
                                  <span>{displayLearningArea(dept.department)}</span>
                                  <span className="text-meta text-ink-500">({deptIntentions.length} staff)</span>
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                {currentFTE.toFixed(1)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                {projectedFTE.toFixed(1)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                <span className={difference >= 0 ? 'text-ok-fg' : 'text-danger-fg'}>
                                  {difference >= 0 ? '+' : ''}
                                  {difference.toFixed(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                                <span className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${
                                  difference > 0.5 ? 'bg-ok-bg text-ok-fg' : 
                                  difference < -0.5 ? 'bg-danger-bg text-danger-fg' : 
                                  'bg-warn-bg text-warn-fg'
                                }`}>
                                  {difference > 0.5 ? 'Growth' : difference < -0.5 ? 'Shortage' : 'Stable'}
                                </span>
                              </td>
                            </tr>
                            
                            {/* Expanded Department Details */}
                            {isExpanded && (
                              <tr>
                                <td colSpan={5} className="px-6 py-4 bg-ink-50">
                                  <div className="space-y-4">
                                    {/* Staff with FTE Changes */}
                                    {staffWithFTEChanges.length > 0 && (
                                      <div>
                                        <h5 className="text-body-sm font-semibold text-ink-900 mb-2">
                                          📊 Staff with FTE Changes ({staffWithFTEChanges.length})
                                        </h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {staffWithFTEChanges.map((staff, staffIndex) => {
                                            const fteChange = staff.targetFTE - staff.currentFTE;
                                            return (
                                              <div key={staffIndex} className="bg-white p-3 rounded-md border border-ink-200">
                                                <div className="flex items-center space-x-2">
                                                  <UserIcon className="h-4 w-4 text-ink-400" />
                                                  <span className="text-body-sm font-medium">{staff.staffName}</span>
                                                </div>
                                                <div className="mt-1 text-meta text-ink-500">
                                                  {staff.currentFTE} → {staff.targetFTE} FTE
                                                  <span className={`ml-2 font-semibold ${
                                                    fteChange > 0 ? 'text-ok-fg' : 'text-danger-fg'
                                                  }`}>
                                                    ({fteChange > 0 ? '+' : ''}{fteChange.toFixed(1)})
                                                  </span>
                                                </div>
                                                <div className="text-meta text-ink-400">
                                                  {staff.desiredEmploymentStatus}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Leaving Staff */}
                                    {leavingStaff.length > 0 && (
                                      <div>
                                        <h5 className="text-body-sm font-semibold text-danger-fg mb-2">
                                          ❌ Staff Not Returning ({leavingStaff.length}) - {leavingStaff.reduce((sum, s) => sum + s.currentFTE, 0).toFixed(1)} FTE
                                        </h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {leavingStaff.map((staff, staffIndex) => (
                                            <div key={staffIndex} className="bg-danger-bg p-3 rounded-md border border-danger-border">
                                              <div className="flex items-center space-x-2">
                                                <UserIcon className="h-4 w-4 text-danger-solid" />
                                                <span className="text-body-sm font-medium text-danger-fg">{staff.staffName}</span>
                                              </div>
                                              <div className="mt-1 text-meta text-danger-fg">
                                                Current: {staff.currentFTE} FTE
                                              </div>
                                              <div className="text-meta text-danger-fg">
                                                {staff.currentEmploymentType}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Uncertain Staff */}
                                    {uncertainStaff.length > 0 && (
                                      <div>
                                        <h5 className="text-body-sm font-semibold text-warn-fg mb-2">
                                          ❓ Uncertain Staff ({uncertainStaff.length}) - {uncertainStaff.reduce((sum, s) => sum + s.currentFTE, 0).toFixed(1)} FTE at risk
                                        </h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {uncertainStaff.map((staff, staffIndex) => (
                                            <div key={staffIndex} className="bg-warn-bg p-3 rounded-md border border-warn-border">
                                              <div className="flex items-center space-x-2">
                                                <UserIcon className="h-4 w-4 text-warn-solid" />
                                                <span className="text-body-sm font-medium text-warn-fg">{staff.staffName}</span>
                                              </div>
                                              <div className="mt-1 text-meta text-warn-fg">
                                                Current: {staff.currentFTE} FTE
                                              </div>
                                              <div className="text-meta text-warn-fg">
                                                {staff.currentEmploymentType}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* No Response Staff */}
                                    {noResponseStaff.length > 0 && (
                                      <div>
                                        <h5 className="text-body-sm font-semibold text-ink-700 mb-2">
                                          ⏳ No Response ({noResponseStaff.length}) - {noResponseStaff.reduce((sum, s) => sum + s.currentFTE, 0).toFixed(1)} FTE pending
                                        </h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                          {noResponseStaff.map((staff, staffIndex) => (
                                            <div key={staffIndex} className="bg-ink-50 p-3 rounded-md border border-ink-200">
                                              <div className="flex items-center space-x-2">
                                                <UserIcon className="h-4 w-4 text-ink-400" />
                                                <span className="text-body-sm font-medium text-ink-900">{staff.staffName}</span>
                                              </div>
                                              <div className="mt-1 text-meta text-ink-500">
                                                Current: {staff.currentFTE} FTE
                                              </div>
                                              <div className="text-meta text-ink-500">
                                                {staff.currentEmploymentType}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Summary for department */}
                                    <div className="mt-4 pt-3 border-t border-ink-200">
                                      <div className="text-meta text-ink-500">
                                        <strong>Learning Area Summary:</strong> {deptIntentions.length} total staff •
                                        {returningStaff.length} confirmed returning • 
                                        {leavingStaff.length} leaving • 
                                        {uncertainStaff.length} uncertain • 
                                        {noResponseStaff.length} no response
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </div>}
        {/* Preferences Tab */}
        {activeTab === 'preferences' && <div className="space-y-6">
            {/* Teaching Preferences */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Teaching Preferences Analysis
                </h3>
                <div className="bg-navy-50 border-l-4 border-navy-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <HelpCircleIcon className="h-5 w-5 text-navy-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-body-sm text-navy-700">
                        This section analyzes teaching preferences to help with
                        subject allocation planning. Use this to identify
                        popular subjects and potential gaps in teaching
                        coverage.
                      </p>
                    </div>
                  </div>
                </div>
                {filteredIntentions.length > 0 ? <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-ink-200">
                      <thead className="bg-ink-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                            Learning Area
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                            First Choice
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                            Second Choice
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                            Third+ Choice
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-ink-200">
                        {/* Calculate subject preferences */}
                        {(() => {
                    const subjectPreferences: Record<string, {
                      first: number;
                      second: number;
                      other: number;
                    }> = {};
                    // Count preferences by rank
                    filteredIntentions.forEach(intention => {
                      intention.teachingPreferences.forEach(pref => {
                        const subject = pref.learningArea;
                        if (!subjectPreferences[subject]) {
                          subjectPreferences[subject] = {
                            first: 0,
                            second: 0,
                            other: 0
                          };
                        }
                        if (pref.rank === 1) {
                          subjectPreferences[subject].first++;
                        } else if (pref.rank === 2) {
                          subjectPreferences[subject].second++;
                        } else {
                          subjectPreferences[subject].other++;
                        }
                      });
                    });
                    // Convert to array and sort by total preferences
                    return Object.entries(subjectPreferences).map(([subject, counts]) => ({
                      subject,
                      first: counts.first,
                      second: counts.second,
                      other: counts.other,
                      total: counts.first + counts.second + counts.other
                    })).sort((a, b) => b.total - a.total).map((item, index) => <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-body-sm font-medium text-ink-900">
                                  {displayLearningArea(item.subject)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                  {item.first}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                  {item.second}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                  {item.other}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                                  {item.total}
                                </td>
                              </tr>);
                  })()}
                      </tbody>
                    </table>
                  </div> : <div className="text-center py-8 text-ink-500">
                    No preference data available for the selected filters.
                  </div>}
              </div>
            </Card>
          </div>}
          
        {/* Insights Tab */}
        {activeTab === 'insights' && <div className="space-y-6">
            {/* Key Insights */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Key Insights for {selectedYear}
                </h3>
                <div className="space-y-4">
                  {/* Retention Risk Analysis */}
                  <div className="bg-warn-bg border border-warn-border rounded-lg p-4">
                    <h4 className="text-body-sm font-semibold text-warn-fg mb-2">⚠️ Retention Risk Analysis</h4>
                    <p className="text-body-sm text-warn-fg">
                      {overviewStats.retentionRisk > 0 ? (
                        `${overviewStats.retentionRisk} staff members (${Math.round((overviewStats.retentionRisk / overviewStats.totalStaff) * 100)}%) have not confirmed their return or are uncertain. ` +
                        `This represents ${(Number(overviewStats.leavingStaffFTE) + Number(overviewStats.uncertainStaffFTE)).toFixed(1)} FTE at risk.`
                      ) : (
                        'All staff have provided clear intentions regarding their return status.'
                      )}
                    </p>
                  </div>
                  
                  {/* FTE Impact */}
                  <div className={`${Number(overviewStats.fteDifference) >= 0 ? 'bg-ok-bg border-ok-border' : 'bg-danger-bg border-danger-border'} border rounded-lg p-4`}>
                    <h4 className={`text-body-sm font-semibold mb-2 ${Number(overviewStats.fteDifference) >= 0 ? 'text-ok-fg' : 'text-danger-fg'}`}>
                      {Number(overviewStats.fteDifference) >= 0 ? '📈' : '📉'} FTE Capacity Analysis
                    </h4>
                    <p className={`text-body-sm ${Number(overviewStats.fteDifference) >= 0 ? 'text-ok-fg' : 'text-danger-fg'}`}>
                      {Number(overviewStats.fteDifference) >= 0 ? (
                        `Returning staff are requesting ${overviewStats.fteDifference} more FTE than their current allocation, indicating potential for growth or increased capacity.`
                      ) : (
                        `Returning staff are requesting ${Math.abs(Number(overviewStats.fteDifference))} less FTE than their current allocation, representing a potential reduction in teaching capacity.`
                      )}
                    </p>
                  </div>
                  
                  {/* Completion Rate */}
                  <div className={`${notSubmittedReport.submissionRate >= 80 ? 'bg-ok-bg border-ok-border' : notSubmittedReport.submissionRate >= 60 ? 'bg-warn-bg border-warn-border' : 'bg-danger-bg border-danger-border'} border rounded-lg p-4`}>
                    <h4 className={`text-body-sm font-semibold mb-2 ${
                      notSubmittedReport.submissionRate >= 80 ? 'text-ok-fg' :
                      notSubmittedReport.submissionRate >= 60 ? 'text-warn-fg' : 'text-danger-fg'
                    }`}>
                      📋 Response Collection Status
                    </h4>
                    <p className={`text-body-sm ${
                      notSubmittedReport.submissionRate >= 80 ? 'text-ok-fg' :
                      notSubmittedReport.submissionRate >= 60 ? 'text-warn-fg' : 'text-danger-fg'
                    }`}>
                      {notSubmittedReport.submissionRate}% of expected staff have submitted their teaching intentions.
                      {notSubmittedReport.submissionRate < 80 && `Consider following up with the remaining ${notSubmittedReport.notSubmittedCount} staff members to ensure complete planning data.`}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Department Risk Analysis */}
            <Card>
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-card-title leading-6 text-ink-900 mb-4">
                  Learning Area Risk Analysis
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Risk Level
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          At Risk Staff
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Recommendations
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {departmentData.map((dept, index) => {
                        const atRiskCount = dept.notReturning + dept.uncertain + (dept.total - dept.returning - dept.notReturning - dept.uncertain);
                        const riskLevel = atRiskCount === 0 ? 'Low' : atRiskCount <= 2 ? 'Medium' : 'High';
                        const riskColor = riskLevel === 'Low' ? 'text-ok-fg bg-ok-bg' : riskLevel === 'Medium' ? 'text-warn-fg bg-warn-bg' : 'text-danger-fg bg-danger-bg';
                        
                        return (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm font-medium text-ink-900">
                              {displayLearningArea(dept.department)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                              <span className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${riskColor}`}>
                                {riskLevel}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                              {atRiskCount} of {dept.total}
                            </td>
                            <td className="px-6 py-4 text-body-sm text-ink-500">
                              {riskLevel === 'High' && 'Immediate attention required for staffing planning'}
                              {riskLevel === 'Medium' && 'Monitor closely and consider contingency planning'}
                              {riskLevel === 'Low' && 'Department appears stable for next year'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          </div>}
        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <IntentionsSubmissionsTable intentions={filteredIntentions} />
        )}
        </>
        )}
      </div>
    </div>;
};
export default TeachingIntentionsReports;